/**
 * Internationalisation.
 *
 * One locale ships — `es-PE` — but every visible string goes through a key
 * from day one. Retrofitting i18n across an admin-heavy application later is
 * a multi-week mechanical refactor; doing it now costs a `t()` call.
 *
 * Keys are English (they are identifiers); values are Spanish (they are
 * content). See CLAUDE.md for the boundary that matters: UI chrome is
 * translated here, but domain content — position names, manual titles,
 * catalogue labels — comes from the API, because the manual requires HR to
 * edit it at runtime.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { isDev } from '@/config/env';

import common from './locales/es-PE/common.json';
import auth from './locales/es-PE/auth.json';
import organization from './locales/es-PE/organization.json';
import employees from './locales/es-PE/employees.json';
import home from './locales/es-PE/home.json';
import validation from './locales/es-PE/validation.json';
import themes from './locales/es-PE/themes.json';
import evaluations from './locales/es-PE/evaluations.json';
import iam from './locales/es-PE/iam.json';
import notifications from './locales/es-PE/notifications.json';
import staffing from './locales/es-PE/staffing.json';
import news from './locales/es-PE/news.json';
import scheduling from './locales/es-PE/scheduling.json';
import navigation from './locales/es-PE/navigation.json';

export const defaultNS = 'common';

export const resources = {
  'es-PE': {
    common,
    auth,
    organization,
    employees,
    home,
    validation,
    themes,
    evaluations,
    iam,
    notifications,
    staffing,
    news,
    scheduling,
    navigation,
  },
} as const;

void i18n.use(initReactI18next).init({
  resources,
  lng: 'es-PE',
  fallbackLng: 'es-PE',
  defaultNS,
  ns: [
    'common',
    'auth',
    'organization',
    'employees',
    'home',
    'themes',
    'validation',
    'evaluations',
    'iam',
    'notifications',
    'staffing',
    'news',
    'scheduling',
    'navigation',
  ],
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
  // A missing key must be loud in development and harmless in production.
  saveMissing: false,
  parseMissingKeyHandler: (key) => (isDev ? `⚠️ ${key}` : key),
});

export default i18n;
